import React, { useState } from "react";
import { Avatar, Box, Typography } from "@mui/material";
import {
  FaLinkedin,
  FaFacebook,
  FaWhatsapp,
  FaEnvelope,
  FaLink,
  FaCheck,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";

import FadeInImage from "../../../components/ui/FadeInImage";
import BlogRichTextReadOnly from "./BlogRichTextReadOnly";
import { Article } from "../services/mockData";

interface Props {
  article: Article;
}

const capitalizeFirstLetter = (value: string) => {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
};

export const formatReadingTime = (minutes?: number) => {
  if (!minutes) return "Lectura rápida";
  const unit = minutes === 1 ? "min." : "mins.";
  return `${minutes} ${unit} de lectura`;
};

export const formatArticleDate = (value?: string) => {
  const cleanedValue = value?.trim();

  if (!cleanedValue) return "Fecha no disponible";

  const alreadyFormattedMatch = cleanedValue.match(
    /^(\d{1,2})\s+de\s+([A-Za-zÁÉÍÓÚáéíóúÑñ]+)\s+de\s+(\d{4})$/,
  );
  if (alreadyFormattedMatch) {
    return `${alreadyFormattedMatch[1]} de ${capitalizeFirstLetter(alreadyFormattedMatch[2])} de ${alreadyFormattedMatch[3]}`;
  }

  const match = cleanedValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const parsedDate = match 
    ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
    : new Date(cleanedValue);

  if (!Number.isNaN(parsedDate.getTime())) {
    const parts = new Intl.DateTimeFormat("es-ES", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).formatToParts(parsedDate);

    const day = parts.find((part) => part.type === "day")?.value ?? "";
    const month = parts.find((part) => part.type === "month")?.value ?? "";
    const year = parts.find((part) => part.type === "year")?.value ?? "";

    return `${day} de ${capitalizeFirstLetter(month)} de ${year}`;
  }

  return cleanedValue;
};

const ArticleDetailContent: React.FC<Props> = ({ article }) => {
  const [copied, setCopied] = useState(false);
  const { t, i18n } = useTranslation();

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 2000);
  };

  // Formateo de fecha inteligente según el idioma actual
  const dateValue = article.fechaCreacion || article.date;
  let formattedDate = "";
  if (dateValue) {
    const parsed = new Date(dateValue);
    if (!Number.isNaN(parsed.getTime())) {
      const locale = i18n.language === "en" ? "en-US" : "es-ES";
      formattedDate = parsed.toLocaleDateString(locale, {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } else {
      // Fallback a la lógica original si no es una fecha válida ISO
      formattedDate = formatArticleDate(dateValue);
    }
  }

  return (
    <Box component="article" className="blog-article-wrapper">
      <Box component="header" sx={{ mb: "25px" }}>
        <Typography
          sx={{
            color: "#0069a7",
            fontWeight: 700,
            fontSize: "0.75rem",
            textTransform: "uppercase",
            mb: 1,
          }}
        >
          {article.category}
        </Typography>

        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: "1.8rem", sm: "2.4rem", md: "2.8rem" },
            fontWeight: 800,
            color: "#002e4d",
            mb: 2,
            lineHeight: 1.2,
          }}
        >
          {article.title}
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            color: "#64748b",
            fontSize: "0.85rem",
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Avatar
              sx={{
                width: 24,
                height: 24,
                bgcolor: "#0069a7",
                color: "#fff",
                fontSize: "0.7rem",
                fontWeight: 700,
              }}
            >
              {article.authorAvatar}
            </Avatar>

            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {t("blog.author")}
            </Typography>
          </Box>

          <Typography>|</Typography>

          <Typography>
            {t("blog.readTime", { time: article.tiempoDeLectura || 1 })}
          </Typography>

          <Typography>|</Typography>

          <Typography>{formattedDate}</Typography>
        </Box>
      </Box>

      {article.image && (
        <Box
          sx={{
            width: "100%",
            aspectRatio: { xs: "4/3", md: "16/9" },
            maxHeight: "500px",
            borderRadius: "8px",
            overflow: "hidden",
            marginBottom: "30px",
            border: "1px solid #e2e8f0",
            backgroundColor: "#f8fafc",
          }}
        >
          <FadeInImage
            src={article.image}
            alt={article.title}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        </Box>
      )}

      <Box
        className="tiptap-content"
        sx={{
          fontSize: "1.1rem",
          lineHeight: 1.7,
          color: "#1e293b",
        }}
      >
        <BlogRichTextReadOnly content={article.content} />
      </Box>

      <Box
        className="share-article-section"
        sx={{ mt: 4, pt: 2, borderTop: "1px solid #e2e8f0" }}
      >
        <Typography
          className="share-article-title"
          sx={{ fontWeight: 600, mb: 2 }}
        >
          {t("blog.share")}
        </Typography>
        <Box
          className="share-buttons-list"
          sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}
        >
          <Box
            component="a"
            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="share-button linkedin"
          >
            <FaLinkedin />
            <span>LinkedIn</span>
          </Box>

          <Box
            component="a"
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="share-button facebook"
          >
            <FaFacebook />
            <span>Facebook</span>
          </Box>

          <Box
            component="a"
            href={`https://api.whatsapp.com/send?text=${encodeURIComponent(article.title + " " + window.location.href)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="share-button whatsapp"
          >
            <FaWhatsapp />
            <span>WhatsApp</span>
          </Box>

          <Box
            component="a"
            href={`mailto:?subject=${encodeURIComponent(article.title)}&body=${encodeURIComponent(window.location.href)}`}
            className="share-button email"
          >
            <FaEnvelope />
            <span>Email</span>
          </Box>

          <Box
            component="button"
            onClick={handleCopyLink}
            className="share-button copy"
            sx={{
              background: "none",
              fontFamily: "inherit",
              border: "1px solid transparent",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            {copied ? <FaCheck style={{ color: "#25d366" }} /> : <FaLink />}
            <span>
              {copied
                ? i18n.language === "en"
                  ? "Copied!"
                  : "¡Copiado!"
                : t("blog.copyUrl")}
            </span>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ArticleDetailContent;
